import * as THREE from 'three';

interface Range {
  start: number;
  count: number;
}
interface Page {
  key: string;
  mesh: THREE.Mesh;
  attributes: Record<string, THREE.BufferAttribute>;
  capacity: number;
  next: number;
  live: number;
  free: Range[];
  bounds: THREE.Box3;
}
interface Slot extends Range {
  page: Page;
}
export interface StaticUpload {
  position: Float32Array;
  normal: Float32Array;
  bounds: THREE.Box3;
}
const vector = new THREE.Vector3();
const normalMatrix = new THREE.Matrix3();

/** Fixed scenery is baked into local pages. Stable IDs own vertex ranges:
 * removing one range collapses only those triangles, immediately opening the
 * rendered hole. Free ranges are reused; empty pages release their resources.
 * No per-object transform, cull or sort loop is needed for thousands of settled
 * fragments. Dynamic bodies use the separate moving batches.
 */
export class StaticClusters {
  readonly root = new THREE.Group();
  private pages = new Set<Page>();
  private pagesByKey = new Map<string, Set<Page>>();
  private owners = new Map<number, Slot[]>();
  private pageGrows = 0;
  private growthCopyVertices = 0;
  constructor(private materials: THREE.Material[]) {}
  /** Grow only a populated spatial/material page, keeping its mesh and owner
   * ranges stable. This reduces submissions without enlarging culling regions
   * or reserving large empty buffers for sparsely occupied tiles. */
  private grow(page: Page, needed: number): void {
    const capacity = Math.min(131072, 2 ** Math.ceil(Math.log2(needed))),
      previous = page.mesh.geometry,
      geometry = new THREE.BufferGeometry();
    for (const name of ['position', 'normal', 'color']) {
      const values = new Float32Array(capacity * 3);
      values.set((page.attributes[name].array as Float32Array).subarray(0, page.next * 3));
      const attribute = new THREE.BufferAttribute(values, 3).setUsage(THREE.DynamicDrawUsage);
      page.attributes[name] = attribute;
      geometry.setAttribute(name, attribute);
    }
    geometry.setDrawRange(0, page.next);
    geometry.boundingBox = page.bounds.clone();
    geometry.boundingSphere = page.bounds.getBoundingSphere(new THREE.Sphere());
    page.mesh.geometry = geometry;
    page.capacity = capacity;
    this.pageGrows++;
    this.growthCopyVertices += page.next;
    previous.dispose();
  }
  /** Prepare a single building off-scene. Publishing remains one transaction;
   * vertex transforms and bounds no longer block its final restoration commit. */
  *prepare(geometry: THREE.BufferGeometry, matrix: THREE.Matrix4): Generator<void, StaticUpload> {
    const source = geometry.getAttribute('position') as THREE.BufferAttribute,
      normals = geometry.getAttribute('normal') as THREE.BufferAttribute,
      position = new Float32Array(source.count * 3),
      normal = new Float32Array(source.count * 3),
      basis = new THREE.Matrix3().getNormalMatrix(matrix),
      bounds = new THREE.Box3();
    const input = source.array,
      inputNormals = normals.array,
      e = matrix.elements,
      n = basis.elements;
    let minX = Infinity,
      minY = Infinity,
      minZ = Infinity,
      maxX = -Infinity,
      maxY = -Infinity,
      maxZ = -Infinity;
    for (let i = 0; i < input.length; i += 3) {
      const x = input[i],
        y = input[i + 1],
        z = input[i + 2];
      const w = 1 / (e[3] * x + e[7] * y + e[11] * z + e[15]);
      const X = (e[0] * x + e[4] * y + e[8] * z + e[12]) * w,
        Y = (e[1] * x + e[5] * y + e[9] * z + e[13]) * w,
        Z = (e[2] * x + e[6] * y + e[10] * z + e[14]) * w;
      position[i] = X;
      position[i + 1] = Y;
      position[i + 2] = Z;
      minX = Math.min(minX, X);
      minY = Math.min(minY, Y);
      minZ = Math.min(minZ, Z);
      maxX = Math.max(maxX, X);
      maxY = Math.max(maxY, Y);
      maxZ = Math.max(maxZ, Z);
      const nx = inputNormals[i],
        ny = inputNormals[i + 1],
        nz = inputNormals[i + 2],
        NX = n[0] * nx + n[3] * ny + n[6] * nz,
        NY = n[1] * nx + n[4] * ny + n[7] * nz,
        NZ = n[2] * nx + n[5] * ny + n[8] * nz,
        inverseLength = 1 / (Math.sqrt(NX * NX + NY * NY + NZ * NZ) || 1);
      normal[i] = NX * inverseLength;
      normal[i + 1] = NY * inverseLength;
      normal[i + 2] = NZ * inverseLength;
      if (i % 6144 === 6141) yield;
    }
    bounds.min.set(minX, minY, minZ);
    bounds.max.set(maxX, maxY, maxZ);
    return { position, normal, bounds };
  }
  set(id: number, geometry: THREE.BufferGeometry, matrix: THREE.Matrix4, upload?: StaticUpload): void {
    this.remove(id);
    const slots: Slot[] = [];
    const x = Math.floor(matrix.elements[12] / 128),
      z = Math.floor(matrix.elements[14] / 128);
    normalMatrix.getNormalMatrix(matrix);
    const e = matrix.elements;
    const translated =
      e[0] === 1 &&
      e[5] === 1 &&
      e[10] === 1 &&
      e[1] === 0 &&
      e[2] === 0 &&
      e[4] === 0 &&
      e[6] === 0 &&
      e[8] === 0 &&
      e[9] === 0;
    let transformedBounds = upload?.bounds;
    if (!transformedBounds && translated) {
      geometry.computeBoundingBox();
      transformedBounds = geometry.boundingBox!.clone().translate(new THREE.Vector3(e[12], e[13], e[14]));
    }
    for (const group of geometry.groups) {
      if (!group.count) continue;
      const key = `${x},${z},${group.materialIndex ?? 0}`;
      let page: Page | undefined, range: Range | undefined, growable: Page | undefined;
      for (const candidate of this.pagesByKey.get(key) ?? []) {
        const index = candidate.free.findIndex((r) => r.count >= group.count);
        if (index >= 0) {
          page = candidate;
          const free = candidate.free[index];
          range = { start: free.start, count: group.count };
          free.start += group.count;
          free.count -= group.count;
          if (!free.count) candidate.free.splice(index, 1);
          break;
        }
        if (candidate.capacity - candidate.next >= group.count) {
          page = candidate;
          range = { start: candidate.next, count: group.count };
          candidate.next += group.count;
          break;
        }
        if (!growable && candidate.capacity < 131072 && candidate.next + group.count <= 131072)
          growable = candidate;
      }
      if (!page && growable) {
        page = growable;
        this.grow(page, page.next + group.count);
        range = { start: page.next, count: group.count };
        page.next += group.count;
      }
      if (!page) {
        const capacity = Math.max(32768, 2 ** Math.ceil(Math.log2(group.count)));
        const g = new THREE.BufferGeometry();
        const attributes: Record<string, THREE.BufferAttribute> = {};
        for (const name of ['position', 'normal', 'color']) {
          attributes[name] = new THREE.BufferAttribute(new Float32Array(capacity * 3), 3).setUsage(
            THREE.DynamicDrawUsage,
          );
          g.setAttribute(name, attributes[name]);
        }
        const mesh = new THREE.Mesh(g, this.materials[group.materialIndex ?? 0] ?? this.materials[0]);
        mesh.castShadow = !mesh.material.transparent;
        mesh.receiveShadow = true;
        mesh.matrixAutoUpdate = false;
        page = {
          key,
          mesh,
          attributes,
          capacity,
          next: group.count,
          live: 0,
          free: [],
          bounds: new THREE.Box3(),
        };
        range = { start: 0, count: group.count };
        this.pages.add(page);
        let keyedPages = this.pagesByKey.get(key);
        if (!keyedPages) this.pagesByKey.set(key, (keyedPages = new Set()));
        keyedPages.add(page);
        this.root.add(mesh);
      }
      const { start, count } = range!;
      for (const name of ['position', 'normal', 'color']) {
        const source = geometry.getAttribute(name) as THREE.BufferAttribute;
        const attribute = page.attributes[name];
        const output = attribute.array as Float32Array;
        if (upload && (name === 'position' || name === 'normal')) {
          output.set(upload[name].subarray(group.start * 3, (group.start + count) * 3), start * 3);
          if (name === 'position') page.bounds.union(upload.bounds);
        } else if (name === 'color' || (name === 'normal' && translated))
          output.set(source.array.subarray(group.start * 3, (group.start + count) * 3), start * 3);
        else if (name === 'position' && translated) {
          const input = source.array;
          for (let i = 0; i < count * 3; i += 3) {
            const at = group.start * 3 + i,
              to = start * 3 + i;
            output[to] = input[at] + e[12];
            output[to + 1] = input[at + 1] + e[13];
            output[to + 2] = input[at + 2] + e[14];
          }
          page.bounds.union(transformedBounds!);
        } else
          for (let i = 0; i < count; i++) {
            vector.fromBufferAttribute(source, group.start + i);
            if (name === 'position') {
              vector.applyMatrix4(matrix);
              page.bounds.expandByPoint(vector);
            } else vector.applyMatrix3(normalMatrix).normalize();
            vector.toArray(output, (start + i) * 3);
          }
        attribute.addUpdateRange(start * 3, count * 3);
        attribute.needsUpdate = true;
      }
      page.live += count;
      page.mesh.geometry.setDrawRange(0, page.next);
      page.mesh.geometry.boundingBox = page.bounds.clone();
      page.mesh.geometry.boundingSphere = page.bounds.getBoundingSphere(new THREE.Sphere());
      slots.push({ page, start, count });
    }
    this.owners.set(id, slots);
  }
  remove(id: number): void {
    for (const { page, start, count } of this.owners.get(id) ?? []) {
      const position = page.attributes.position;
      // All vertices in the released triangles coincide. Their old faces cannot
      // survive a topology edit while another part of the page stays visible.
      (position.array as Float32Array).fill(0, start * 3, (start + count) * 3);
      position.addUpdateRange(start * 3, count * 3);
      position.needsUpdate = true;
      page.live -= count;
      page.free.push({ start, count });
      page.free.sort((a, b) => a.start - b.start);
      const merged: Range[] = [];
      for (const range of page.free) {
        const last = merged[merged.length - 1];
        if (last && last.start + last.count === range.start) last.count += range.count;
        else merged.push({ ...range });
      }
      page.free = merged;
      const tail = page.free[page.free.length - 1];
      if (tail && tail.start + tail.count === page.next) {
        page.next = tail.start;
        page.free.pop();
        page.mesh.geometry.setDrawRange(0, page.next);
      }
      if (!page.live) {
        this.root.remove(page.mesh);
        page.mesh.geometry.dispose();
        this.pages.delete(page);
        const keyedPages = this.pagesByKey.get(page.key)!;
        keyedPages.delete(page);
        if (!keyedPages.size) this.pagesByKey.delete(page.key);
      }
    }
    this.owners.delete(id);
  }
  clear(): void {
    for (const step of this.clearSteps()) void step;
  }
  *clearSteps(): Generator<void> {
    for (const page of this.pages) {
      this.root.remove(page.mesh);
      page.mesh.geometry.dispose();
      yield;
    }
    this.root.clear();
    this.pages.clear();
    this.pagesByKey.clear();
    this.owners.clear();
    this.pageGrows = this.growthCopyVertices = 0;
  }
  get stats(): Record<string, number> {
    let vertices = 0,
      capacity = 0;
    for (const page of this.pages) {
      vertices += page.live;
      capacity += page.capacity;
    }
    return {
      pages: this.pages.size,
      owners: this.owners.size,
      vertices,
      capacity,
      pageGrows: this.pageGrows,
      growthCopyVertices: this.growthCopyVertices,
    };
  }
}
